import React, { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import usePagination from "../../../../hooks/usePagination";
import { CollapseWithControls, NotesTableWithActions } from "../../../../components/ui";
import { adaptSaveNoteToReactQuery } from "../../../../components/ui/NotesTableWithActions";
import { FUNDED_COMPLIANCE_API_URLS, FundedComplianceAPI } from "../../../../api/fundedCompliance";
import { fundedComplianceProfileActions } from "../../../../reducers/fundedComplianceProfile";

const Notes: React.FC = () => {
  const queryClient = useQueryClient();
  const pagination = usePagination(4);
  const params: { id: string } = useParams();
  const id = +params.id;
  const dispatch = useDispatch();
  const {
    data: { rows, active_notes_count },
  } = useSelector((state) => state.fundedComplianceProfile.notes);

  const handleOnSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [FUNDED_COMPLIANCE_API_URLS.notes],
    });
  };

  const { mutate: handleCreateNote } = useMutation({
    mutationKey: [],
    mutationFn: async (note: string) => await FundedComplianceAPI.createNoteRequest({ note }, id),
    onSuccess: handleOnSuccess,
  });

  const { mutate: handleUpdateNote } = useMutation({
    mutationKey: [],
    mutationFn: async (body: { id: number; is_active: boolean }) =>
      await FundedComplianceAPI.updateNoteRequest(body),
    onSuccess: handleOnSuccess,
  });

  const { mutate: handleDeleteNote } = useMutation({
    mutationKey: [],
    mutationFn: async (id: number) => await FundedComplianceAPI.deleteNoteRequest(id),
    onSuccess: handleOnSuccess,
  });

  const { isError, error } = useQuery({
    queryKey: [FUNDED_COMPLIANCE_API_URLS.notes, id, pagination.limit, pagination.page],
    queryFn: async ({ signal }) => {
      const query = new URLSearchParams();
      query.append("limit", pagination.limit.toString());
      query.append("page", pagination.page.toString());
      dispatch(fundedComplianceProfileActions.fetchNotesRequest());
      const response = await FundedComplianceAPI.getNotesRequest(id, query.toString(), signal);
      pagination.setCount(response.data.count);
      dispatch(fundedComplianceProfileActions.fetchNotesSuccess(response.data));
      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      dispatch(fundedComplianceProfileActions.fetchNotesFailed());
    }
  }, [isError, error]);

  return (
    <div>
      <CollapseWithControls
        buttonText={`Notes: ${active_notes_count}`}
        buttonProps={{ ...(active_notes_count && { color: "secondary" }) }}
      >
        <NotesTableWithActions
          notes={rows}
          pagination={pagination}
          saveNote={(note) => adaptSaveNoteToReactQuery(note, handleCreateNote)}
          handleChangeStatus={(noteId: number, is_active: boolean) =>
            adaptSaveNoteToReactQuery({ id: noteId, is_active }, handleUpdateNote)
          }
          handleDeleteNote={(id) => adaptSaveNoteToReactQuery(id, handleDeleteNote)}
          isAllowedChangeStatusAndRemoveActions
        />
      </CollapseWithControls>
    </div>
  );
};

export default Notes;
